import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ToolCall } from '@/types/chat';

interface ToolCallItemProps {
  toolCall: ToolCall;
  index: number;
}

function getMethodVariant(method: string): string {
  switch (method.toUpperCase()) {
    case 'GET':
      return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300';
    case 'POST':
      return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300';
    case 'PUT':
    case 'PATCH':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300';
    case 'DELETE':
      return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300';
    default:
      return 'bg-secondary text-secondary-foreground';
  }
}

function getStatusVariant(statusCode: number): string {
  if (statusCode >= 200 && statusCode < 300) {
    return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300';
  }
  return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300';
}

export function ToolCallItem({ toolCall, index }: ToolCallItemProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-md border bg-card">
      {/* Always visible row */}
      <button
        type="button"
        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <span className="flex-none text-xs font-mono text-muted-foreground w-5 text-right">
          {index}
        </span>

        <span className="flex-1 truncate text-xs font-mono">{toolCall.endpoint}</span>

        <span
          className={cn(
            'flex-none rounded border px-1.5 py-0.5 text-xs font-semibold',
            getMethodVariant(toolCall.method),
          )}
        >
          {toolCall.method.toUpperCase()}
        </span>

        <span
          className={cn(
            'flex-none rounded border px-1.5 py-0.5 text-xs font-semibold',
            getStatusVariant(toolCall.statusCode),
          )}
        >
          {toolCall.statusCode}
        </span>

        <span className="flex-none text-xs text-muted-foreground w-16 text-right">
          {toolCall.durationMs}ms
        </span>

        {expanded ? (
          <ChevronDown className="flex-none h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="flex-none h-3.5 w-3.5 text-muted-foreground" />
        )}
      </button>

      {/* Expandable payload section */}
      {expanded && (
        <div className="border-t px-3 py-2 space-y-2">
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Request</p>
            <pre className="text-xs font-mono bg-muted p-2 rounded overflow-auto max-h-80 whitespace-pre-wrap break-all leading-relaxed">
              {JSON.stringify(toolCall.requestPayload, null, 2)}
            </pre>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Response</p>
            <pre className="text-xs font-mono bg-muted p-2 rounded overflow-auto max-h-80 whitespace-pre-wrap break-all leading-relaxed">
              {JSON.stringify(toolCall.responsePayload, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
